/**
 * Translation glossary for medical/clinical terminology.
 * Used to guide the translation LLM to prefer consistent terms.
 * Structure: key = language pair "fromLang→toLang", value = array of { source, target }.
 * Can be extended to load from Firestore or config at runtime.
 */

const MEDICAL_GLOSSARY = {
  'en-US→sw-KE': [
    { source: 'blood pressure', target: 'shinikizo la damu' },
    { source: 'diabetes', target: 'kisukari' },
    { source: 'allergy', target: 'mzio' },
    { source: 'penicillin', target: 'penicillin' },
    { source: 'antibiotics', target: 'antibiotiki' },
    { source: 'infection', target: 'maambukizi' },
    { source: 'surgery', target: 'upasuaji' },
    { source: 'pregnant', target: 'mimba' },
    { source: 'asthma', target: 'pumu' },
  ],
  'sw-KE→en-US': [
    { source: 'shinikizo la damu', target: 'blood pressure' },
    { source: 'kisukari', target: 'diabetes' },
    { source: 'mzio', target: 'allergy' },
    { source: 'antibiotiki', target: 'antibiotics' },
    { source: 'maambukizi', target: 'infection' },
    { source: 'upasuaji', target: 'surgery' },
    { source: 'mimba', target: 'pregnant' },
    { source: 'pumu', target: 'asthma' },
  ],
  'en-US→es-ES': [
    { source: 'blood pressure', target: 'presión arterial' },
    { source: 'diabetes', target: 'diabetes' },
    { source: 'allergy', target: 'alergia' },
    { source: 'penicillin', target: 'penicilina' },
    { source: 'antibiotics', target: 'antibióticos' },
    { source: 'infection', target: 'infección' },
    { source: 'surgery', target: 'cirugía' },
    { source: 'pregnant', target: 'embarazada' },
    { source: 'asthma', target: 'asma' },
  ],
  'es-ES→en-US': [
    { source: 'presión arterial', target: 'blood pressure' },
    { source: 'alergia', target: 'allergy' },
    { source: 'penicilina', target: 'penicillin' },
    { source: 'antibióticos', target: 'antibiotics' },
    { source: 'infección', target: 'infection' },
    { source: 'cirugía', target: 'surgery' },
    { source: 'embarazada', target: 'pregnant' },
  ],
  'en-US→fr-FR': [
    { source: 'blood pressure', target: 'tension artérielle' },
    { source: 'diabetes', target: 'diabète' },
    { source: 'allergy', target: 'allergie' },
    { source: 'penicillin', target: 'pénicilline' },
    { source: 'antibiotics', target: 'antibiotiques' },
    { source: 'infection', target: 'infection' },
    { source: 'surgery', target: 'chirurgie' },
    { source: 'pregnant', target: 'enceinte' },
    { source: 'asthma', target: 'asthme' },
  ],
  'fr-FR→en-US': [
    { source: 'tension artérielle', target: 'blood pressure' },
    { source: 'diabète', target: 'diabetes' },
    { source: 'allergie', target: 'allergy' },
    { source: 'pénicilline', target: 'penicillin' },
    { source: 'antibiotiques', target: 'antibiotics' },
    { source: 'chirurgie', target: 'surgery' },
    { source: 'enceinte', target: 'pregnant' },
    { source: 'asthme', target: 'asthma' },
  ],
};

/**
 * Get glossary entries for a language pair.
 * @param {string} fromLang - Source language code (e.g. 'en-US')
 * @param {string} toLang - Target language code (e.g. 'sw-KE')
 * @returns {Array<{ source: string, target: string }>}
 */
export const getGlossary = (fromLang, toLang) => {
  const key = `${fromLang}→${toLang}`;
  return MEDICAL_GLOSSARY[key] || [];
};

/**
 * Optional: merge runtime glossary (e.g. from Firestore) with built-in.
 * @param {Array<{ source: string, target: string }>} runtimeEntries - Additional entries
 * @param {string} fromLang
 * @param {string} toLang
 * @returns {Array<{ source: string, target: string }>}
 */
export const getGlossaryWithOverrides = (runtimeEntries, fromLang, toLang) => {
  const builtIn = getGlossary(fromLang, toLang);
  if (!runtimeEntries || runtimeEntries.length === 0) return builtIn;
  const bySource = new Map(builtIn.map((e) => [e.source.toLowerCase(), e]));
  runtimeEntries.forEach((e) => bySource.set((e.source || '').toLowerCase(), e));
  return Array.from(bySource.values());
};

export default { getGlossary, getGlossaryWithOverrides, MEDICAL_GLOSSARY };
